import { useEffect, useMemo, useState } from 'react';
import BranchService from '../../services/branch/BranchService';
import RestaurantReportService from '../../services/restaurant/RestaurantReportService';
import useAlerts from '../useAlerts';
import useApiErrorHandler from '../useApiErrorHandler';
import useLoading from '../useLoading';
import { useAuthUser } from '../auth/useAuthUser';
import {
  fallbackRestaurantReports,
  restaurantReportFiltersMap,
  restaurantStaticReportOptions,
} from '../../features/restaurant/reports/restaurantReportCatalog';
import { IBranchDropdown } from '../../types/branches/branchInterfaces';
import {
  RestaurantReportFilterConfig,
  RestaurantReportFormat,
  RestaurantReportOptionsMap,
  RestaurantReportTypeDTO,
} from '../../types/restaurant/RestaurantReportTypes';

const today = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
};

const firstDayOfMonth = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${now.getFullYear()}-${month}-01`;
};

const normalizeValue = (
  value: string,
  type: RestaurantReportFilterConfig['type']
) => {
  if (type === 'number' || (type === 'select' && /^\d+$/.test(value))) {
    return Number(value);
  }

  return value;
};

const mergeOptions = (
  dynamicOptions: RestaurantReportOptionsMap
): RestaurantReportOptionsMap => ({
  ...restaurantStaticReportOptions,
  ...dynamicOptions,
  waiters: dynamicOptions.waiters ?? [],
});

const useRestaurantReports = () => {
  const { show } = useAlerts();
  const { handle } = useApiErrorHandler();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { hasRole } = useAuthUser();
  const canPickBranch =
    hasRole('ADMIN') || hasRole('SUPPORT') || hasRole('ACCOUNTANT');

  const [branches, setBranches] = useState<IBranchDropdown[]>([]);
  const [branchId, setBranchId] = useState<number | ''>('');
  const [reports, setReports] = useState<RestaurantReportTypeDTO[]>([]);
  const [selectedReportId, setSelectedReportId] = useState<number | ''>('');
  const [format, setFormat] = useState<RestaurantReportFormat>('XLSX');
  const [filters, setFilters] = useState<Record<string, string>>({
    dateFrom: firstDayOfMonth(),
    dateTo: today(),
  });
  const [options, setOptions] = useState<RestaurantReportOptionsMap>(
    restaurantStaticReportOptions
  );
  const [loading, setLoading] = useState(true);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedReport = useMemo(
    () => reports.find((report) => report.id === selectedReportId) || null,
    [reports, selectedReportId]
  );

  const selectedFilters = useMemo(() => {
    const code = selectedReport?.code;

    if (!code) return [];

    return (
      restaurantReportFiltersMap[
        code as keyof typeof restaurantReportFiltersMap
      ] || []
    );
  }, [selectedReport]);

  const filtersWithOptions = useMemo(
    () =>
      selectedFilters.map((filter) => ({
        ...filter,
        options: filter.optionSource ? options[filter.optionSource] || [] : [],
      })),
    [options, selectedFilters]
  );

  useEffect(() => {
    setLoading(true);
    setError(null);

    Promise.all([
      canPickBranch
        ? BranchService.fetchDropdown()
        : BranchService.fetchMyBranch().then((mine) => [mine]),
      RestaurantReportService.listTypes(),
    ])
      .then(([branchResponse, reportResponse]) => {
        const branchItems = branchResponse || [];
        const backendReports = reportResponse.data || [];
        const waiterTipsReport = fallbackRestaurantReports.find(
          (report) => report.code === 'RESTAURANT_WAITER_TIPS'
        );
        const reportItems = backendReports.length
          ? [
              ...backendReports,
              ...(waiterTipsReport &&
              !backendReports.some(
                (report) => report.code === 'RESTAURANT_WAITER_TIPS'
              )
                ? [waiterTipsReport]
                : []),
            ]
          : fallbackRestaurantReports;

        setBranches(branchItems);
        setBranchId((current) => current || branchItems[0]?.id || '');
        setReports(
          [...reportItems].sort(
            (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
          )
        );
      })
      .catch(() => {
        setReports(fallbackRestaurantReports);
        setError(
          'No se pudo cargar el catalogo desde backend. Se muestra una propuesta local para continuar el diseno.'
        );
      })
      .finally(() => setLoading(false));
  }, [canPickBranch]);

  useEffect(() => {
    if (!branchId) return;

    setFilters((current) => {
      const { waiterId, ...remaining } = current;
      return remaining;
    });
    setOptionsLoading(true);
    RestaurantReportService.getOptions(branchId)
      .then((response) => setOptions(mergeOptions(response.data || {})))
      .catch(() => setOptions(restaurantStaticReportOptions))
      .finally(() => setOptionsLoading(false));
  }, [branchId]);

  const updateFilter = (name: string, value: string) => {
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const resetFilters = () => {
    setFilters({
      dateFrom: firstDayOfMonth(),
      dateTo: today(),
    });
  };

  const clearSelection = () => {
    setSelectedReportId('');
    resetFilters();
  };

  const generateReport = async () => {
    if (exporting) return;

    try {
      if (!selectedReport || !branchId) {
        show(
          'warning',
          'Reporte requerido',
          'Seleccione sucursal y tipo de reporte.'
        );
        return;
      }

      const missing = selectedFilters.filter(
        (filter) => filter.required && !filters[filter.name]
      );

      if (missing.length) {
        show(
          'warning',
          'Filtros incompletos',
          `Complete: ${missing.map((filter) => filter.label).join(', ')}.`
        );
        return;
      }

      if (
        filters.dateFrom &&
        filters.dateTo &&
        filters.dateFrom > filters.dateTo
      ) {
        show(
          'warning',
          'Rango de fechas inválido',
          'La fecha inicial no puede ser posterior a la fecha final.'
        );
        return;
      }

      const parsedFilters = selectedFilters.reduce<
        Record<string, string | number>
      >((payload, filter) => {
        const value = filters[filter.name];

        if (value !== undefined && value !== '') {
          payload[filter.name] = normalizeValue(value, filter.type);
        }

        return payload;
      }, {});

      showLoadingIndicator();
      setExporting(true);

      await RestaurantReportService.export({
        reportCode: selectedReport.code,
        branchId,
        format,
        filters: parsedFilters,
      });

      show('success', 'Reporte generado', 'La descarga inicio correctamente.');
    } catch (error) {
      handle(error);
    } finally {
      setExporting(false);
      hideLoadingIndicator();
    }
  };

  return {
    branches,
    branchId,
    setBranchId,
    canPickBranch,
    reports,
    selectedReportId,
    setSelectedReportId,
    selectedReport,
    filters,
    filtersWithOptions,
    format,
    setFormat,
    loading,
    optionsLoading,
    exporting,
    error,
    updateFilter,
    resetFilters,
    clearSelection,
    generateReport,
  };
};

export default useRestaurantReports;
